import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { PolicyException } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, policyApi } from "../../lib/api";
import { PolicyDemoBanner } from "../../components/PolicyMode";

const STATUS_TONE: Record<string, "neutral" | "success" | "warning" | "danger"> = {
  requested: "warning",
  changes_requested: "warning",
  approved: "success",
  rejected: "danger",
  expired: "danger",
  closed: "neutral",
};

interface FormState {
  policyId: string;
  title: string;
  businessJustification: string;
  riskOwnerId: string;
  requestedStart: string;
  requestedEnd: string;
}

const EMPTY: FormState = {
  policyId: "",
  title: "",
  businessJustification: "",
  riskOwnerId: "",
  requestedStart: "",
  requestedEnd: "",
};

export function PolExceptions() {
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [form, setForm] = useState<FormState>(EMPTY);
  const [rationale, setRationale] = useState("");
  const [decisionStart, setDecisionStart] = useState("");
  const [decisionEnd, setDecisionEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const [exceptions, policies, members] = await Promise.all([
        policyApi.exceptions(orgId),
        policyApi.policies(orgId),
        api.members(orgId),
      ]);
      return { exceptions, policies: policies.data.policies, members: members.members };
    },
    [orgId],
  );
  const exceptions = state.data?.exceptions.data.exceptions ?? [];
  const policies = state.data?.policies ?? [];
  const members = state.data?.members ?? [];

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      state.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }

  const complete =
    form.policyId.trim() &&
    form.title.trim() &&
    form.businessJustification.trim() &&
    form.riskOwnerId.trim() &&
    form.requestedStart &&
    form.requestedEnd;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Exceptions &amp; waivers</h1>
          <p>Deviations from policy with a time limit and a named risk owner: requested, decided, renewed and closed with evidence.</p>
        </div>
      </div>

      <PolicyDemoBanner show={state.data?.exceptions.demoMode ?? false} />
      {error && <ErrorState description={error} />}

      <Card>
        <CardHeader title="Request an exception" />
        <CardBody>
          <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
            <Field label="Policy">
              {(id) => (
                <Select id={id} value={form.policyId} onChange={(e) => set("policyId", e.target.value)}>
                  <option value="">Select a policy…</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Title">
              {(id) => <TextInput id={id} value={form.title} onChange={(e) => set("title", e.target.value)} />}
            </Field>
            <Field label="Risk owner">
              {(id) => (
                <Select id={id} value={form.riskOwnerId} onChange={(e) => set("riskOwnerId", e.target.value)}>
                  <option value="">Select a teammate…</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.email}
                      {m.id === user?.id ? " (you)" : ""}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Start">
              {(id) => <TextInput id={id} type="date" value={form.requestedStart} onChange={(e) => set("requestedStart", e.target.value)} />}
            </Field>
            <Field label="End">
              {(id) => <TextInput id={id} type="date" value={form.requestedEnd} onChange={(e) => set("requestedEnd", e.target.value)} />}
            </Field>
            <Field label="Business justification">
              {(id) => (
                <TextArea
                  id={id}
                  rows={2}
                  value={form.businessJustification}
                  onChange={(e) => set("businessJustification", e.target.value)}
                />
              )}
            </Field>
            <Button
              size="sm"
              disabled={busy || !complete}
              onClick={() =>
                run(async () => {
                  await policyApi.requestException(orgId, {
                    policyId: form.policyId.trim(),
                    title: form.title.trim(),
                    businessJustification: form.businessJustification.trim(),
                    riskOwnerId: form.riskOwnerId.trim(),
                    requestedStart: form.requestedStart,
                    requestedEnd: form.requestedEnd,
                  });
                  setForm(EMPTY);
                })
              }
            >
              Submit request
            </Button>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading exceptions…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}

      <Card>
        <CardHeader
          title="Exception register"
          action={
            <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
              <Field label="Decision rationale">
                {(id) => <TextInput id={id} value={rationale} onChange={(e) => setRationale(e.target.value)} />}
              </Field>
              <Field label="Approved start">
                {(id) => <TextInput id={id} type="date" value={decisionStart} onChange={(e) => setDecisionStart(e.target.value)} />}
              </Field>
              <Field label="Approved end">
                {(id) => <TextInput id={id} type="date" value={decisionEnd} onChange={(e) => setDecisionEnd(e.target.value)} />}
              </Field>
            </div>
          }
        />
        <CardBody>
          <p className="ods-help">
            An approval may only shorten the requested window. Leave the decision dates blank to grant the window as
            requested; the approved end date is also reused as the new end date when renewing.
          </p>
          {state.data && exceptions.length === 0 ? (
            <EmptyState title="No exceptions" description="Nothing is currently waived against your policies." />
          ) : (
            <DataTable
              columns={[
                { key: "title", header: "Exception", render: (x: PolicyException) => <strong style={{ fontWeight: 600 }}>{x.title}</strong> },
                { key: "status", header: "Status", render: (x: PolicyException) => <Badge tone={STATUS_TONE[x.status] ?? "neutral"}>{x.status}</Badge> },
                {
                  key: "window",
                  header: "Window",
                  render: (x: PolicyException) =>
                    `${formatDate(x.approvedStart ?? x.requestedStart)} → ${formatDate(x.approvedEnd ?? x.requestedEnd)}`,
                },
                {
                  key: "actions",
                  header: "",
                  render: (x: PolicyException) => (
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                      {x.status === "requested" && (
                        <>
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                await policyApi.decideException(orgId, x.id, {
                                  decision: "approved",
                                  rationale: rationale.trim(),
                                  approvedStart: decisionStart || x.requestedStart,
                                  approvedEnd: decisionEnd || x.requestedEnd,
                                });
                                setDecisionStart("");
                                setDecisionEnd("");
                              })
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                await policyApi.decideException(orgId, x.id, {
                                  decision: "rejected",
                                  rationale: rationale.trim(),
                                });
                              })
                            }
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {x.status === "approved" && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                await policyApi.renewException(orgId, x.id, {
                                  requestedStart: x.approvedEnd ?? x.requestedEnd,
                                  requestedEnd:
                                    decisionEnd || new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
                                });
                                setDecisionEnd("");
                              })
                            }
                          >
                            Renew
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => run(async () => { await policyApi.closeException(orgId, x.id, rationale.trim()); })}
                          >
                            Close
                          </Button>
                        </>
                      )}
                    </div>
                  ),
                },
              ]}
              rows={exceptions}
              caption="Policy exceptions"
            />
          )}
        </CardBody>
      </Card>
    </>
  );
}
